import React, { useEffect, useState, useCallback } from 'react';
import { GitCompare, TrendingUp, BarChart3, RefreshCw, Loader2, ArrowUpRight, ArrowDownRight, Minus, ChevronDown, ChevronUp, Info, FlaskConical } from 'lucide-react';
import { adminGet } from '../data/api';

const PIN = 'pointedge2024';
const mono = { fontFamily: 'JetBrains Mono, monospace' };
const heading = { fontFamily: 'Outfit, sans-serif' };
const card = { background: '#18181B', border: '1px solid #27272A' };
const innerCard = { background: '#0D0D0F', border: '1px solid #1F1F22' };

function StatCard({ label, value, sub, color = 'text-zinc-200', icon: Icon }) {
  return (
    <div className="rounded-sm p-4" style={card} data-testid={`stat-${label.replace(/\s/g, '-').toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={13} className="text-zinc-500" />}
        <span className="text-zinc-500 text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <div className={`text-xl font-bold ${color}`} style={mono}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-600 mt-1">{sub}</div>}
    </div>
  );
}

function ModelCard({ title, badge, stats, color }) {
  const borderColor = color === 'emerald' ? '#10B981' : '#6366F1';
  const badgeBg = color === 'emerald' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-indigo-500/10 text-indigo-400';
  return (
    <div className="rounded-sm p-4 flex-1" style={{ ...card, borderTop: `2px solid ${borderColor}` }} data-testid={`model-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-zinc-200 font-semibold text-sm" style={heading}>{title}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${badgeBg}`}>{badge}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {stats.map(s => (
          <div key={s.label} className="rounded-sm p-2" style={innerCard}>
            <div className="text-zinc-500 text-[9px] uppercase tracking-wider">{s.label}</div>
            <div className={`text-sm font-bold mt-0.5 ${s.color || 'text-zinc-200'}`} style={mono}>{s.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PredRow({ row, expanded, onToggle }) {
  const lr = row.lr_final_p1 || 0.5;
  const v2 = row.v2_final_p1 || 0.5;
  const diff = ((lr - v2) * 100).toFixed(1);
  const diffColor = Math.abs(diff) < 1 ? 'text-zinc-500' : diff > 0 ? 'text-indigo-400' : 'text-emerald-400';

  const lrEv = row.lr_recommended_ev || 0;
  const v2Ev = Math.max(row.v2_ev_p1 || 0, row.v2_ev_p2 || 0);
  const lrHasValue = lrEv > 0;
  const v2HasValue = v2Ev > 0;
  const lrSide = lrHasValue ? (row.lr_recommended_side || '-') : null;
  const v2Side = v2HasValue ? ((row.v2_ev_p1 || 0) > (row.v2_ev_p2 || 0) ? 'P1' : 'P2') : null;
  const agree = lrSide && v2Side && lrSide === v2Side;

  const bothNoBet = !lrSide && !v2Side;
  const diverge = lrSide && v2Side && !agree;

  const isFinished = row.match_status === 'finished';
  const winner = row.winner || '';

  const rowBg = agree ? 'bg-emerald-500/8 border-l-2 border-l-emerald-500' : diverge ? 'bg-red-500/5 border-l-2 border-l-red-500/40' : '';

  return (
    <>
      <tr
        className={`text-xs border-t border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer transition-colors ${rowBg}`}
        onClick={onToggle}
        data-testid={`pred-row-${row.match_id}`}
      >
        <td className="py-2 px-2 text-zinc-400 max-w-[200px] truncate">
          <span className="text-zinc-300">{row.player1 || '?'}</span>
          <span className="text-zinc-600 mx-1">vs</span>
          <span className="text-zinc-300">{row.player2 || '?'}</span>
        </td>
        <td className="py-2 px-2 text-right text-indigo-400" style={mono}>{(lr * 100).toFixed(1)}%</td>
        <td className="py-2 px-2 text-right text-emerald-400" style={mono}>{(v2 * 100).toFixed(1)}%</td>
        <td className={`py-2 px-2 text-right ${diffColor}`} style={mono}>{diff > 0 ? '+' : ''}{diff}%</td>
        <td className="py-2 px-2 text-center">
          {agree
            ? <span className="text-emerald-400 text-[10px] font-medium">SI</span>
            : lrSide && v2Side
            ? <span className="text-red-400 text-[10px] font-medium">{lrSide}/{v2Side}</span>
            : lrSide && !v2Side
            ? <span className="text-indigo-400 text-[10px] font-medium">LR</span>
            : !lrSide && v2Side
            ? <span className="text-emerald-400 text-[10px] font-medium">V2</span>
            : <span className="text-zinc-600 text-[10px]">—</span>
          }
        </td>
        <td className="py-2 px-2 text-right text-indigo-300" style={mono}>{lrEv > 0 ? `+${lrEv.toFixed(1)}%` : '-'}</td>
        <td className="py-2 px-2 text-right text-emerald-300" style={mono}>{v2Ev > 0 ? `+${v2Ev.toFixed(1)}%` : '-'}</td>
        <td className="py-2 px-2 text-center">
          {isFinished
            ? <span className="text-amber-400 text-[10px]">{winner ? winner.split(' ')[0] : 'fin.'}</span>
            : <span className="text-zinc-600 text-[10px]">{row.match_status || '-'}</span>
          }
        </td>
        <td className="py-2 px-2 text-center text-zinc-600">
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </td>
      </tr>
      {expanded && (
        <tr className="border-t border-zinc-800/30" data-testid={`pred-detail-${row.match_id}`}>
          <td colSpan={9} className="p-3" style={{ background: '#0D0D0F' }}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[10px]">
              <div>
                <div className="text-zinc-600 uppercase tracking-wider mb-1">LR Features</div>
                {row.lr_features && Object.entries(row.lr_features).map(([k, v]) => (
                  <div key={k} className="flex justify-between py-0.5">
                    <span className="text-zinc-500">{k}</span>
                    <span className="text-zinc-300" style={mono}>{typeof v === 'number' ? v.toFixed(4) : v}</span>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-zinc-600 uppercase tracking-wider mb-1">LR Internals</div>
                <div className="flex justify-between py-0.5"><span className="text-zinc-500">z_score</span><span className="text-indigo-300" style={mono}>{(row.lr_z_score || 0).toFixed(4)}</span></div>
                <div className="flex justify-between py-0.5"><span className="text-zinc-500">model_p1</span><span className="text-indigo-300" style={mono}>{((row.lr_model_p1 || 0) * 100).toFixed(2)}%</span></div>
                <div className="flex justify-between py-0.5"><span className="text-zinc-500">confidence</span><span className="text-indigo-300" style={mono}>{((row.lr_confidence || 0) * 100).toFixed(1)}%</span></div>
                <div className="flex justify-between py-0.5"><span className="text-zinc-500">alpha</span><span className="text-indigo-300" style={mono}>{(row.lr_alpha || 0).toFixed(3)}</span></div>
              </div>
              <div>
                <div className="text-zinc-600 uppercase tracking-wider mb-1">Pascal V2</div>
                <div className="flex justify-between py-0.5"><span className="text-zinc-500">final_p1</span><span className="text-emerald-300" style={mono}>{((row.v2_final_p1 || 0) * 100).toFixed(2)}%</span></div>
                <div className="flex justify-between py-0.5"><span className="text-zinc-500">confidence</span><span className="text-emerald-300" style={mono}>{((row.v2_confidence || 0) * 100).toFixed(1)}%</span></div>
                <div className="flex justify-between py-0.5"><span className="text-zinc-500">ev_p1</span><span className="text-emerald-300" style={mono}>{(row.v2_ev_p1 || 0).toFixed(1)}%</span></div>
                <div className="flex justify-between py-0.5"><span className="text-zinc-500">ev_p2</span><span className="text-emerald-300" style={mono}>{(row.v2_ev_p2 || 0).toFixed(1)}%</span></div>
              </div>
              <div>
                <div className="text-zinc-600 uppercase tracking-wider mb-1">Quote Book</div>
                <div className="flex justify-between py-0.5"><span className="text-zinc-500">P1 odds</span><span className="text-zinc-300" style={mono}>{(row.bookmaker_odd_p1 || 0).toFixed(2)}</span></div>
                <div className="flex justify-between py-0.5"><span className="text-zinc-500">P2 odds</span><span className="text-zinc-300" style={mono}>{(row.bookmaker_odd_p2 || 0).toFixed(2)}</span></div>
                <div className="flex justify-between py-0.5"><span className="text-zinc-500">LR fair P1</span><span className="text-indigo-300" style={mono}>{(row.lr_fair_odds_p1 || 0).toFixed(2)}</span></div>
                <div className="flex justify-between py-0.5"><span className="text-zinc-500">V2 fair P1</span><span className="text-emerald-300" style={mono}>{(row.v2_fair_odds_p1 || 0).toFixed(2)}</span></div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function ModelCompare() {
  const [data, setData] = useState(null);
  const [settled, setSettled] = useState(null);
  const [lrConfig, setLrConfig] = useState(null);
  const [backtest, setBacktest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [btLoading, setBtLoading] = useState(false);
  const [tab, setTab] = useState('live');
  const [expanded, setExpanded] = useState({});
  const [showBetas, setShowBetas] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [comp, sett, cfg] = await Promise.all([
        adminGet('/tt/admin/model-compare?limit=500', PIN),
        adminGet('/tt/admin/model-compare-settled?limit=500&days=30', PIN),
        adminGet('/tt/admin/lr-config', PIN),
      ]);
      setData(comp);
      setSettled(sett);
      setLrConfig(cfg);
    } catch (err) {
      console.error('ModelCompare fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const runBacktest = useCallback(async () => {
    setBtLoading(true);
    try {
      const bt = await adminGet('/tt/admin/lr-rolling-backtest?limit=5000', PIN);
      setBacktest(bt);
    } catch (err) {
      console.error('Backtest error:', err);
    } finally {
      setBtLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const predictions = tab === 'live' ? (data?.predictions || []) : (settled?.matches || []);
  const lrSummary = settled?.lr_summary || {};
  const v2Summary = settled?.v2_summary || {};

  return (
    <div className="space-y-5 max-w-7xl" data-testid="model-compare-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-zinc-100 tracking-tight flex items-center gap-2" style={heading}>
            <GitCompare size={20} className="text-indigo-400" />
            MW Model Compare
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Pascal-Sierpinski v2 vs Logistic Regression v1 — confronto parallelo
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowBetas(!showBetas)}
            data-testid="toggle-betas-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50 transition-colors"
            style={{ border: '1px solid #27272A' }}
          >
            <Info size={12} /> Beta LR
          </button>
          <button
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-compare-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-zinc-800 text-zinc-300 hover:bg-zinc-700 text-xs transition-colors"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Aggiorna
          </button>
        </div>
      </div>

      {/* Beta config panel */}
      {showBetas && lrConfig && (
        <div className="rounded-sm p-4" style={card} data-testid="lr-betas-panel">
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">LR Beta Coefficients (v1 baseline)</div>
          <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
            {Object.entries(lrConfig).filter(([k]) => k.startsWith('beta_') || k === 'intercept').map(([k, v]) => (
              <div key={k} className="rounded-sm p-2 text-center" style={innerCard}>
                <div className="text-[9px] text-zinc-500">{k.replace('beta_', '')}</div>
                <div className="text-sm font-bold text-indigo-300 mt-0.5" style={mono}>{v}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <div className="rounded-sm p-2 text-center" style={innerCard}>
              <div className="text-[9px] text-zinc-500">alpha_min</div>
              <div className="text-sm font-bold text-zinc-300" style={mono}>{lrConfig.alpha_min}</div>
            </div>
            <div className="rounded-sm p-2 text-center" style={innerCard}>
              <div className="text-[9px] text-zinc-500">alpha_max</div>
              <div className="text-sm font-bold text-zinc-300" style={mono}>{lrConfig.alpha_max}</div>
            </div>
            <div className="rounded-sm p-2 text-center" style={innerCard}>
              <div className="text-[9px] text-zinc-500">h2h_target</div>
              <div className="text-sm font-bold text-zinc-300" style={mono}>{lrConfig.h2h_target}</div>
            </div>
          </div>
        </div>
      )}

      {/* Aggregate stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard
          label="Match Analizzati" icon={BarChart3}
          value={data?.total || 0}
          sub="predizioni LR vs V2"
        />
        <StatCard
          label="Entrambi EV+" icon={GitCompare}
          value={data?.both_have_pick || 0}
          sub={`accordo: ${data?.agree_count || 0}/${data?.both_have_pick || 0}`}
          color={data?.agreement_rate > 80 ? 'text-emerald-400' : data?.agreement_rate > 50 ? 'text-amber-400' : 'text-red-400'}
        />
        <StatCard
          label="Solo LR EV+" icon={ArrowUpRight}
          value={data?.lr_only_pick || 0}
          color="text-indigo-400"
        />
        <StatCard
          label="Solo V2 EV+" icon={ArrowUpRight}
          value={data?.v2_only_pick || 0}
          color="text-emerald-400"
        />
        <StatCard
          label="Diff Media Prob" icon={Minus}
          value={`${data?.avg_prob_diff_pct || 0}%`}
          color="text-zinc-300"
          sub="|LR - V2| media"
        />
      </div>

      {/* Settled P&L comparison */}
      {settled && (settled.lr_summary?.picks > 0 || settled.v2_summary?.picks > 0) && (
        <div className="flex gap-3">
          <ModelCard
            title="Logistic Regression" badge="mw_lr_v1" color="indigo"
            stats={[
              { label: 'Picks', value: lrSummary.picks || 0 },
              { label: 'Win Rate', value: `${lrSummary.win_rate || 0}%`, color: lrSummary.win_rate > 55 ? 'text-emerald-400' : 'text-zinc-300' },
              { label: 'Profit', value: `${lrSummary.profit > 0 ? '+' : ''}${lrSummary.profit || 0}u`, color: lrSummary.profit > 0 ? 'text-emerald-400' : 'text-red-400' },
              { label: 'ROI', value: `${lrSummary.roi || 0}%`, color: lrSummary.roi > 0 ? 'text-emerald-400' : 'text-red-400' },
            ]}
          />
          <ModelCard
            title="Pascal-Sierpinski" badge="mw_v2" color="emerald"
            stats={[
              { label: 'Picks', value: v2Summary.picks || 0 },
              { label: 'Win Rate', value: `${v2Summary.win_rate || 0}%`, color: v2Summary.win_rate > 55 ? 'text-emerald-400' : 'text-zinc-300' },
              { label: 'Profit', value: `${v2Summary.profit > 0 ? '+' : ''}${v2Summary.profit || 0}u`, color: v2Summary.profit > 0 ? 'text-emerald-400' : 'text-red-400' },
              { label: 'ROI', value: `${v2Summary.roi || 0}%`, color: v2Summary.roi > 0 ? 'text-emerald-400' : 'text-red-400' },
            ]}
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-zinc-800 pb-0">
        {[
          { id: 'live', label: 'Tutte le predizioni', count: data?.total || 0 },
          { id: 'settled', label: 'Solo settate', count: settled?.settled_count || 0 },
          { id: 'backtest', label: 'Rolling Backtest', count: backtest?.matches_available || '—' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => { setTab(t.id); if (t.id === 'backtest' && !backtest && !btLoading) runBacktest(); }}
            data-testid={`tab-${t.id}`}
            className={`px-3 py-2 text-xs font-medium transition-colors border-b-2 -mb-[1px] ${
              tab === t.id
                ? 'text-indigo-400 border-indigo-400'
                : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }`}
          >
            {t.id === 'backtest' && <FlaskConical size={11} className="inline mr-1 -mt-0.5" />}
            {t.label} <span className="text-zinc-600 ml-1">({t.count})</span>
          </button>
        ))}
      </div>

      {/* Backtest results */}
      {tab === 'backtest' && (
        <BacktestSection data={backtest} loading={btLoading} onRun={runBacktest} />
      )}

      {/* Predictions table */}
      {tab !== 'backtest' && (
      <div className="rounded-sm overflow-hidden" style={card}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={20} className="animate-spin text-indigo-400" />
          </div>
        ) : predictions.length === 0 ? (
          <div className="text-center py-16 text-zinc-600 text-sm">
            Nessuna predizione LR disponibile.<br />
            <span className="text-zinc-700 text-xs">Le predizioni verranno generate al prossimo refresh con match attivi.</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="predictions-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-zinc-500" style={{ background: '#111113' }}>
                  <th className="py-2 px-2 font-medium">Match</th>
                  <th className="py-2 px-2 font-medium text-right text-indigo-400/70">LR P1</th>
                  <th className="py-2 px-2 font-medium text-right text-emerald-400/70">V2 P1</th>
                  <th className="py-2 px-2 font-medium text-right">Diff</th>
                  <th className="py-2 px-2 font-medium text-center">Accordo</th>
                  <th className="py-2 px-2 font-medium text-right text-indigo-400/70">LR EV</th>
                  <th className="py-2 px-2 font-medium text-right text-emerald-400/70">V2 EV</th>
                  <th className="py-2 px-2 font-medium text-center">Stato</th>
                  <th className="py-2 px-2 w-6"></th>
                </tr>
              </thead>
              <tbody>
                {predictions.map(row => (
                  <PredRow
                    key={row.match_id}
                    row={row}
                    expanded={!!expanded[row.match_id]}
                    onToggle={() => toggle(row.match_id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function BacktestSection({ data, loading, onRun }) {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Loader2 size={24} className="animate-spin text-indigo-400" />
        <span className="text-xs text-zinc-500">Esecuzione rolling backtest su 3 configurazioni...</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-16" data-testid="backtest-empty">
        <FlaskConical size={24} className="mx-auto text-zinc-600 mb-3" />
        <div className="text-zinc-500 text-sm mb-2">Rolling Window Backtest</div>
        <p className="text-zinc-600 text-xs mb-4">
          Confronta 3 configurazioni di finestra sul modello LR:<br />
          train=1500/2000/2500, test=250/300, purge gap=30
        </p>
        <button
          onClick={onRun}
          data-testid="run-backtest-btn"
          className="px-4 py-2 rounded-sm bg-indigo-500/15 text-indigo-400 text-xs font-medium hover:bg-indigo-500/25 transition-colors"
          style={{ border: '1px solid rgba(99,102,241,0.2)' }}
        >
          <FlaskConical size={12} className="inline mr-1.5 -mt-0.5" />
          Avvia Backtest
        </button>
      </div>
    );
  }

  if (data.error) {
    return (
      <div className="text-center py-12 text-amber-400 text-sm" data-testid="backtest-error">
        {data.error}<br />
        <span className="text-zinc-600 text-xs">Match disponibili: {data.matches_available || 0}</span>
      </div>
    );
  }

  const comp = data.comparison;
  const configs = data.configs || [];

  return (
    <div className="space-y-4" data-testid="backtest-results">
      {/* Header info */}
      <div className="flex items-center justify-between text-xs text-zinc-500">
        <span>Match totali: <span className="text-zinc-300 font-mono">{data.matches_available}</span> | Periodo: <span className="text-zinc-300">{data.date_range}</span></span>
        <button onClick={onRun} className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
          <RefreshCw size={10} /> Riesegui
        </button>
      </div>

      {/* Comparison table */}
      {comp && comp.table && (
        <div className="rounded-sm overflow-hidden" style={card}>
          <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid #27272A' }}>
            <BarChart3 size={13} className="text-indigo-400" />
            <span className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Confronto Configurazioni</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="backtest-comparison-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-zinc-500" style={{ background: '#111113' }}>
                  <th className="py-2 px-3 text-left font-medium">Config</th>
                  <th className="py-2 px-2 text-center font-medium">Train</th>
                  <th className="py-2 px-2 text-center font-medium">Test</th>
                  <th className="py-2 px-2 text-center font-medium">Folds</th>
                  <th className="py-2 px-2 text-right font-medium">Log Loss</th>
                  <th className="py-2 px-2 text-right font-medium">Brier</th>
                  <th className="py-2 px-2 text-right font-medium">Cal Err</th>
                  <th className="py-2 px-2 text-right font-medium">Hit Rate</th>
                  <th className="py-2 px-2 text-right font-medium">ROI</th>
                  <th className="py-2 px-2 text-right font-medium">PnL</th>
                </tr>
              </thead>
              <tbody>
                {comp.table.map(row => {
                  const isBest = row.config === comp.best_by_metric?.roi;
                  const isDefault = row.config === 'config_2';
                  return (
                    <tr key={row.config} className={`border-t border-zinc-800/50 ${isBest ? 'bg-emerald-500/5' : ''}`}>
                      <td className="py-2 px-3 font-medium text-zinc-300">
                        {row.config.replace('config_', 'C')}
                        {isDefault && <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-indigo-500/10 text-indigo-400">DEFAULT</span>}
                        {isBest && <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-400">BEST ROI</span>}
                      </td>
                      <td className="py-2 px-2 text-center text-zinc-400" style={mono}>{row.train}</td>
                      <td className="py-2 px-2 text-center text-zinc-400" style={mono}>{row.test}</td>
                      <td className="py-2 px-2 text-center text-zinc-400" style={mono}>{row.folds}</td>
                      <td className="py-2 px-2 text-right" style={mono}>
                        <span className={row.config === comp.best_by_metric?.log_loss ? 'text-emerald-400 font-semibold' : 'text-zinc-300'}>
                          {row.log_loss.toFixed(4)}
                        </span>
                        <span className="text-zinc-600 text-[9px] ml-0.5">&plusmn;{row.log_loss_std.toFixed(4)}</span>
                      </td>
                      <td className="py-2 px-2 text-right" style={mono}>
                        <span className={row.config === comp.best_by_metric?.brier_score ? 'text-emerald-400 font-semibold' : 'text-zinc-300'}>
                          {row.brier_score.toFixed(4)}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right text-zinc-400" style={mono}>{row.avg_cal_error.toFixed(4)}</td>
                      <td className="py-2 px-2 text-right" style={mono}>
                        <span className={row.config === comp.best_by_metric?.hit_rate ? 'text-emerald-400 font-semibold' : 'text-zinc-300'}>
                          {row.hit_rate}%
                        </span>
                        <span className="text-zinc-600 text-[9px] ml-0.5">&plusmn;{row.hit_rate_std}</span>
                      </td>
                      <td className={`py-2 px-2 text-right font-semibold ${row.roi > 0 ? 'text-emerald-400' : 'text-red-400'}`} style={mono}>
                        {row.roi > 0 ? '+' : ''}{row.roi}%
                        <span className="text-zinc-600 text-[9px] ml-0.5">&plusmn;{row.roi_std}</span>
                      </td>
                      <td className={`py-2 px-2 text-right font-semibold ${row.pnl > 0 ? 'text-emerald-400' : 'text-red-400'}`} style={mono}>
                        {row.pnl > 0 ? '+' : ''}{row.pnl}u
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Best by metric summary */}
          {comp.best_by_metric && (
            <div className="px-4 py-2 flex flex-wrap gap-3 text-[10px] text-zinc-600" style={{ borderTop: '1px solid #27272A' }}>
              {Object.entries(comp.best_by_metric).map(([metric, cfg]) => (
                <span key={metric}>
                  {metric.replace('_', ' ')}: <span className="text-zinc-400">{cfg?.replace('config_', 'C')}</span>
                </span>
              ))}
              <span className="ml-auto text-zinc-500">
                Default operativo: <span className="text-indigo-400 font-medium">{comp.recommendation?.replace('config_', 'C')}</span>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Per-config fold details */}
      {configs.filter(c => c.aggregate).map(cfg => (
        <ConfigFoldDetail key={cfg.config_name} config={cfg} />
      ))}
    </div>
  );
}

function ConfigFoldDetail({ config }) {
  const [open, setOpen] = useState(false);
  const agg = config.aggregate;
  if (!agg) return null;

  return (
    <div className="rounded-sm overflow-hidden" style={card}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-zinc-800/30 transition-colors"
        data-testid={`config-detail-${config.config_name}`}
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-zinc-300">{config.config_name.replace('config_', 'Config ')}</span>
          <span className="text-[10px] text-zinc-500">
            train={config.config.train} test={config.config.test} step={config.config.step} purge={config.config.purge}
          </span>
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          <span className="text-zinc-400">{agg.n_folds} folds</span>
          <span className="text-zinc-400">{agg.total_predictions} pred</span>
          <span className={agg.roi > 0 ? 'text-emerald-400' : 'text-red-400'} style={mono}>{agg.roi > 0 ? '+' : ''}{agg.roi}%</span>
          {open ? <ChevronUp size={12} className="text-zinc-500" /> : <ChevronDown size={12} className="text-zinc-500" />}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3" style={{ borderTop: '1px solid #27272A' }}>
          {/* Fold table */}
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-[10px]">
              <thead>
                <tr className="text-zinc-600 uppercase tracking-wider">
                  <th className="py-1.5 px-2 text-left">Fold</th>
                  <th className="py-1.5 px-2 text-left">Test Range</th>
                  <th className="py-1.5 px-2 text-right">Preds</th>
                  <th className="py-1.5 px-2 text-right">Bets</th>
                  <th className="py-1.5 px-2 text-right">Log Loss</th>
                  <th className="py-1.5 px-2 text-right">Brier</th>
                  <th className="py-1.5 px-2 text-right">Cal Err</th>
                  <th className="py-1.5 px-2 text-right">HR</th>
                  <th className="py-1.5 px-2 text-right">ROI</th>
                  <th className="py-1.5 px-2 text-right">PnL</th>
                </tr>
              </thead>
              <tbody>
                {(config.folds || []).map(f => (
                  <tr key={f.fold} className="border-t border-zinc-800/30">
                    <td className="py-1.5 px-2 text-zinc-400" style={mono}>#{f.fold}</td>
                    <td className="py-1.5 px-2 text-zinc-500" style={mono}>{f.test_range}</td>
                    <td className="py-1.5 px-2 text-right text-zinc-400" style={mono}>{f.n_predictions}</td>
                    <td className="py-1.5 px-2 text-right text-zinc-400" style={mono}>{f.n_bets}</td>
                    <td className="py-1.5 px-2 text-right text-zinc-300" style={mono}>{f.log_loss.toFixed(4)}</td>
                    <td className="py-1.5 px-2 text-right text-zinc-300" style={mono}>{f.brier_score.toFixed(4)}</td>
                    <td className="py-1.5 px-2 text-right text-zinc-400" style={mono}>{f.avg_cal_error.toFixed(4)}</td>
                    <td className="py-1.5 px-2 text-right text-zinc-300" style={mono}>{f.hit_rate}%</td>
                    <td className={`py-1.5 px-2 text-right font-semibold ${f.roi > 0 ? 'text-emerald-400' : 'text-red-400'}`} style={mono}>
                      {f.roi > 0 ? '+' : ''}{f.roi}%
                    </td>
                    <td className={`py-1.5 px-2 text-right ${f.pnl > 0 ? 'text-emerald-400' : 'text-red-400'}`} style={mono}>
                      {f.pnl > 0 ? '+' : ''}{f.pnl}u
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Aggregate summary */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-[10px]">
            {[
              { l: 'Log Loss Range', v: `${agg.log_loss_range[0].toFixed(4)} — ${agg.log_loss_range[1].toFixed(4)}` },
              { l: 'ROI Range', v: `${agg.roi_range[0]}% — ${agg.roi_range[1]}%` },
              { l: 'Total PnL', v: `${agg.total_pnl > 0 ? '+' : ''}${agg.total_pnl}u`, c: agg.total_pnl > 0 ? 'text-emerald-400' : 'text-red-400' },
              { l: 'Avg Hit Rate', v: `${agg.hit_rate}%` },
              { l: 'Brier Score', v: agg.brier_score.toFixed(4) },
              { l: 'Avg Cal Error', v: agg.avg_cal_error.toFixed(4) },
            ].map(s => (
              <div key={s.l} className="rounded-sm p-2" style={innerCard}>
                <div className="text-zinc-600 uppercase tracking-wider text-[8px]">{s.l}</div>
                <div className={`font-bold mt-0.5 ${s.c || 'text-zinc-300'}`} style={mono}>{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
